/**
 * Game Configuration
 *
 * Central configuration file containing ALL numeric constants used in the game.
 * NO MAGIC NUMBERS IN CODE - all values must be defined here!
 *
 * Benefits:
 * - Single source of truth for game balance
 * - Easy tuning and experimentation
 * - Foundation for UI-based configuration (ConfigWindow)
 * - Self-documenting through descriptive constant names
 *
 * Organization:
 * - GAME_CONFIG: Core gameplay mechanics (health, speed, damage, leveling)
 * - AI_CONFIG: AI behavior parameters (fire rate, perception, courage)
 * - COMBAT_CONFIG: Weapon mechanics and combat calculations
 * - MOVEMENT_CONFIG: Speed multipliers and movement thresholds
 * - RESOURCE_CONFIG: Resource spawn rates and values
 * - ANIMATION_CONFIG: Visual effects and animation speeds
 * - UI_CONFIG: Fonts, colors, and rendering parameters
 * - THRESHOLD_CONFIG: Percentage thresholds and timings
 * - BASE_CONFIG: Base-specific parameters
 * - BERSERKER_CONFIG: Max-level unit configuration
 * - TEAM_CONFIG: Team definitions and colors
 * - CANVAS_CONFIG: Canvas dimensions and settings
 * - DEBUG_VISUALIZATION_CONFIG: Debug rendering options
 *
 * Usage:
 * ```javascript
 * import { GAME_CONFIG, AI_CONFIG } from './config.js';
 * if (unit.health < unit.maxHealth * GAME_CONFIG.FLEE_HEALTH_THRESHOLD) {
 *   // flee logic
 * }
 * ```
 *
 * See also: js/utils/configMetadata.js for UI generation metadata
 */

// Core gameplay mechanics
export const GAME_CONFIG = {
  BASE_SPEED: 0.75,
  BASE_SIZE: 4,
  BASE_HEALTH: 100,
  BASE_WEAPON_RANGE: 80,
  BASE_DAMAGE: 2,
  FLEE_HEALTH_THRESHOLD: 0.3, // Flee at 30% health
  LEVEL_UP_MULTIPLIER: 1.15, // Reduced from 1.2 for better balance (1.15^14 = 6.26x vs 1.2^14 = 19.12x)
  MAX_LEVEL: 15,
  INITIAL_UNITS_PER_TEAM: 50, // Reduced for debugging
  SPAWN_MARGIN: 50, // Margin from screen edges for initial spawn
  AMMO_CAPACITY: 100, // Shots before needing to resupply
  RESOURCE_CARRY_SPEED_MULTIPLIER: 0.6, // Speed modifier when carrying resources
  MAX_TOTAL_UNITS: 300, // Global unit limit across all teams
  MAX_UNITS_PER_TEAM: 10 // Per-team unit limit
};

// Debug configuration
export const DEBUG_CONFIG = {
  targetingLines: true,
  predictedPositions: true,
  stats: true
};

// Debug visualization configuration
export const DEBUG_VISUALIZATION_CONFIG = {
  // Unit visualization
  showPerceptionRadius: false,
  showWeaponRadius: false,
  showUnitState: true,
  showTargetLines: true,
  showPredictedPositions: true,
  
  // Future options placeholder
  showPathfinding: false,
  showCollisionBoxes: false,
  showHealthBars: true,
  showAmmoCount: true,
  
  // Colors for debug visualizations
  colors: {
    perceptionRadius: 'rgba(0, 255, 0, 0.2)',
    weaponRadius: 'rgba(255, 0, 0, 0.2)',
    unitStateText: '#0f0',
    pathfinding: 'rgba(255, 255, 0, 0.5)',
    collisionBox: 'rgba(0, 255, 255, 0.3)'
  }
};

// Projectile configuration
export const PROJECTILE_CONFIG = {
  BASE_SPEED: 2,
  TRAIL_LENGTH: 12,
  MAX_LIFETIME: 200,
  GLOW_BLUR: 15,
  CORE_BLUR: 20,
  // Base missile specific
  MISSILE_SIZE_MULTIPLIER: 2.5, // Base missiles are bigger
  MISSILE_TRAIL_LENGTH: 20, // Longer exhaust trail
  MISSILE_EXHAUST_PARTICLES: 3, // Number of exhaust particles per frame
  MISSILE_EXPLOSION_RADIUS: 30, // Explosion effect radius
  MISSILE_EXPLOSION_DURATION: 15 // Frames for explosion animation
};

// AI configuration
export const AI_CONFIG = {
  FIRE_RATE: 500, // ms between shots
  MIN_FIRE_RATE: 200, // minimum fire rate for veterans
  HEAL_DELAY: 2000, // 2 seconds before healing starts
  HEAL_RATE: 10, // health per update when healing
  HEAL_TIME: 1000, // ms to fully heal inside base
  RESUPPLY_TIME: 5000, // ms to fully resupply ammo
  MAX_COURAGE: 100,
  SKILL_RANGE: { min: 10, max: 20 }, // Starting skill range
  MAX_VELOCITY_CAP: 5, // Cap for velocity predictions
  PREDICTION_MARGIN: 50, // Edge margin for prediction checks
  MAX_OFF_SCREEN_PREDICTIONS: 10,
  TARGET_CHECK_INTERVAL: 1000, // ms between target re-evaluation
  TARGET_SWITCH_THRESHOLD: 0.7, // Switch if new target is 30% closer
  SEPARATION_RADIUS: 18, // Distance to maintain from teammates
  SEPARATION_FORCE: 0.3, // Strength of separation behavior
  MAX_PREDICTION_FACTOR: 0.6, // Cap on prediction to prevent over-leading
  PERCEPTION_RANGE: 80, // Default perception range (same as weapon range)
  PERCEPTION_LEVEL_BONUS: 0.2, // 20% of default range per level
  CORPSE_COLLECTION_RADIUS: 25, // Distance to collect corpse
  CORPSE_SEEK_PRIORITY: 1.0, // How much AI prioritizes corpses vs combat (0-1)
  CORPSE_DECAY_TIME: 30000, // Time before corpse disappears (30 seconds)
  BASE_AVOIDANCE_RADIUS: 80, // Distance to start avoiding enemy bases
  BASE_AVOIDANCE_FORCE: 0.4, // Strength of base avoidance behavior
  BASE_TARGETING_MIN_LEVEL: 10, // Minimum level to target bases opportunistically
  BASE_HUNTING_MIN_LEVEL: 12, // Minimum level to actively hunt bases
  BASE_TARGET_RANGE_MULTIPLIER: 1.5 // Range multiplier for base targeting
};



// Team configuration
export const TEAM_CONFIG = {
  TEAMS: [
    { id: 0, name: 'Red', color: '#ff4444' },
    { id: 1, name: 'Blue', color: '#4444ff' },
    { id: 2, name: 'Green', color: '#44ff44' },
    { id: 3, name: 'Yellow', color: '#ffff44' },
    { id: 4, name: 'Pink', color: '#ff55ff' }
  ],
  DEFAULT_TEAM: 0
};

// Rendering configuration
export const RENDER_CONFIG = {
  HEALTH_BAR_HEIGHT: 3,
  COURAGE_BAR_HEIGHT: 2,
  EDGE_MARGIN: 50,
  SKILL_DOT_SIZE: 2,
  SKILL_DOT_SPACING: 4
};

// Base structure configuration
export const BASE_CONFIG = {
  SIZE: 40,
  HEALTH: 1000,
  WEAPON_RANGE: 180,
  FIRE_RATE: 533, // 50% faster fire rate (was 800ms)
  DAMAGE: 8,
  CENTER_THRESHOLD: 30, // Distance from base center to be considered "at center"
  SPAWN_INTERVAL: 30000, // Time between unit spawns in ms
  REPAIR_TIME: 15000, // ms to fully repair when a unit is inside

  // Base leveling system
  STARTING_LEVEL: 5,              // Start equivalent to level 5 soldier
  MAX_LEVEL: 12,                  // Cap to prevent invincibility
  KILLS_PER_LEVEL: 3,             // Missile kills needed per level
  LEVEL_UP_MULTIPLIER: 1.15,      // Same as GAME_CONFIG.LEVEL_UP_MULTIPLIER
  FIRE_RATE_IMPROVEMENT: 0.9,     // Fire rate multiplier per level (faster = lower value)
  
  // Soldier repair mechanics
  MAX_CONCURRENT_REPAIRS: 5, // Maximum soldiers that can repair a base at once
  SOLDIER_REPAIR_RATE: 0.5, // HP per second per soldier repairing
  REPAIR_RANGE: 20, // Distance from base center to repair
  
  // Soldier cargo system
  SOLDIER_CARGO_CAPACITY: 5, // Maximum metal units a soldier can carry
  
  METAL_MAX: 500, // Maximum metal a base can store
  METAL_LOW_THRESHOLD: 100, // Below this, base will request metal
  DEFEND_PRIORITY_TIME: 5000, // ms to prioritize defense after a hit
  PERCEPTION_RANGE: 160, // Double the unit default (2x 80)
  PERCEPTION_KILL_BONUS: 16, // 20% of unit default range (20% of 80) per missile kill
  MISSILE_AMMO_COST: 5, // Each missile costs 5 soldier ammo units
  MAX_AMMO: 500, // Maximum ammo a base can store (100 missiles worth)
  STARTING_AMMO: 250, // Start with 50 missiles worth of ammo
  
  // Endgame features
  ENABLE_AMMO_REGEN: true, // Allow bases to slowly regenerate ammo when critical
  CRITICAL_AMMO_THRESHOLD: 0.1, // 10% ammo triggers regeneration
  AMMO_REGEN_RATE: 0.5, // Ammo per second when regenerating
  
  // Tractor beam for metal collection
  // Note: TRACTOR_BEAM_RANGE is calculated dynamically as 90% of current perception range (scales with leveling)
  TRACTOR_BEAM_SPEED: 0.75, // Speed for pulling metal/crystal resources

  // Hybrid recovery system
  ORGANICS_PER_MISSILE_KILL: 1,     // Organics gained from each missile kill
  CORPSE_TRACTOR_RANGE: 100,        // Range to pull corpses
  CORPSE_TRACTOR_SPEED: 0.3,        // Speed corpses are pulled
  CORPSE_CONVERT_DELAY: 15000,      // ms before corpse converts to organics (15 seconds)
  CORPSE_ORGANICS_VALUE: 5,         // Organics gained from converting a corpse
  EMERGENCY_ORGANICS_RATE: 0.05,    // Organics per second when critical (3 per minute)
  CRITICAL_SOLDIER_COUNT: 0         // Soldier count threshold for emergency generation
};

// Radio system configuration (team coordination via RadioSystem)
export const RADIO_CONFIG = {
  // Base defense requests
  BASE_DEFENSE_ENABLED: true,               // Enable base defense coordination
  BASE_DEFENSE_HEALTH_THRESHOLD: 0.5,       // Broadcast defense when health < 50%
  BASE_DEFENSE_REQUEST_INTERVAL: 5000,      // Min ms between defense broadcasts (prevent spam)
  BASE_DEFENSE_REQUEST_TTL: 10000,          // Defense request lifetime (10 seconds)
  BASE_DEFENSE_MAX_RESPONDERS: 5,           // Maximum soldiers responding to one defense request
  BASE_DEFENSE_UTILITY_THRESHOLD: 0.5,      // Min score to respond to defense (lower than assist 0.55)
  BASE_DEFENSE_MAX_DISTANCE: 800,           // Max distance soldiers will respond from
  BASE_DEFENSE_ARRIVAL_RADIUS: 60,          // Distance from base to be "defending"
  BASE_DEFENSE_PATROL_RADIUS: 100,          // Patrol radius around base when defending

  // Assist requests (existing system)
  ASSIST_REQUEST_TTL: 6000,                 // Assist request lifetime (6 seconds)
  ASSIST_MIN_BROADCAST_INTERVAL: 2000,      // Min ms between assist broadcasts
  ASSIST_UTILITY_THRESHOLD: 0.55,           // Min score to respond to assist
  ASSIST_MAX_DISTANCE: 600,                 // Max distance for assist response
  ASSIST_ARRIVAL_RADIUS: 48,                // Distance to assist location
  ASSIST_ENGAGE_RADIUS: 160                 // Search radius for enemies at assist location
};

// Resource configuration
export const RESOURCE_CONFIG = {
  SPAWN_INTERVAL: 200, // Metal spawn rate
  METAL_TO_AMMO: 50,
  MAX_METAL_COUNT: 100, // Maximum number of metal resources on map at once
  
  // Nebula organics
  NEBULA_SPAWN_INTERVAL: 35000, // Spawn every 35 seconds
  NEBULA_RADIUS: 25, // Visual radius of the nebula cloud
  NEBULA_GATHER_RATE: 1, // Organics per second per gatherer
  NEBULA_MAX_GATHERERS: 5, // Max simultaneous gatherers
  ORGANICS_PER_UNIT: 10, // Organics needed to spawn one unit
  MAX_NEBULA_COUNT: 3, // Maximum nebulas on map at once
  NEBULA_TOTAL_ORGANICS: 75, // Total organics per nebula (7.5 units worth)
  NEBULA_FADE_TIME: 5000, // Time to fade out when depleted (ms)
  
  // Crystal cores
  CRYSTAL_SPAWN_INTERVAL: 150000, // Spawn every 2.5 minutes
  CRYSTAL_UNITS_GRANTED: 3, // Units spawned when crystal is used
  MAX_CRYSTAL_COUNT: 2, // Maximum crystals on map at once
  CRYSTAL_GRANTS_AMMO: true, // Whether crystals also grant ammo to base
  CRYSTAL_AMMO_GRANTED: 100 // Amount of ammo granted by crystal (20 missiles worth)
};

// Berserker mode configuration (for max level soldiers)
export const BERSERKER_CONFIG = {
  DAMAGE_MULTIPLIER: 2.0,        // Double damage output
  SPEED_MULTIPLIER: 1.5,         // 50% faster movement
  FIRE_RATE_MULTIPLIER: 0.5,     // Shoot twice as fast
  BASE_DESTRUCTION_LEVELS: 5,    // Levels gained from destroying a base
  BASE_PRIORITY_RANGE: 2.0,      // Prioritize bases within 2x weapon range
  GLOW_COLOR: '#ff0000',         // Red glow effect
  GLOW_INTENSITY: 30,            // Shadow blur amount
  GLOW_RADIUS: 30,               // Glow radius for berserker effect
  SIZE_MULTIPLIER: 1.2,          // Size increase for berserker mode
  GLOW_RENDER_PASSES: 3,         // Number of glow render passes
  GLOW_MIN_INTENSITY: 0.7,       // Minimum glow intensity
  GLOW_VARIATION: 0.3,           // Glow variation amount
  GLOW_SPEED: 0.005              // Glow animation speed
};

// Movement and physics configuration
export const MOVEMENT_CONFIG = {
  // Speed multipliers
  SOLDIER_SPEED_MIN: 0.8,         // Minimum speed variation
  SOLDIER_SPEED_MAX: 1.2,         // Maximum speed variation
  LEVEL_SPEED_MULTIPLIER: 1.1,    // Speed increase per level
  FLEE_SPEED_MULTIPLIER: 1.5,     // Speed when fleeing
  URGENT_SPEED_MULTIPLIER: 1.2,   // Speed for urgent movement
  QUICK_SPEED_MULTIPLIER: 1.1,    // Speed for quick movement
  PATROL_SPEED_MULTIPLIER: 0.7,   // Speed when patrolling
  WANDER_SPEED_MULTIPLIER: 0.8,   // Speed when wandering
  RESOURCE_APPROACH_SPEED: 0.3,   // Speed when approaching distant resources
  RETURN_RESOURCE_SPEED: 0.6,     // Speed when returning resources
  
  // Movement thresholds
  MIN_VELOCITY_THRESHOLD: 0.1,    // Minimum velocity before stopping
  KNOCKBACK_VELOCITY_DECAY: 0.75, // Velocity decay during knockback
  KNOCKBACK_DURATION: 30,         // Knockback duration in frames
  BOIDS_MOVEMENT_REDUCTION: 0.3,  // Movement reduction while gathering
  CENTER_MOVEMENT_DECAY: 0.9,     // Velocity decay near base center
  
  // Patrol and combat movement
  MAX_TURN_RATE: 0.05,            // Maximum radians per frame for smooth turning
  PATROL_ANGLE_INCREMENT: 0.02,   // Patrol rotation speed
  COMBAT_ANGLE_INCREMENT: 0.05,   // Combat strafe rotation speed
  SAFE_DIRECTION_CHECKS: 8,       // Number of directions to check for safety
  SAFE_DIRECTION_DISTANCE: 100,   // Distance to check for safe directions
  EDGE_SAFETY_WEIGHT: 0.1,        // Weight for edge avoidance
  
  // Separation forces
  ALLY_SEPARATION_RADIUS: 25,     // Distance to maintain from allies
  ALLY_SEPARATION_FORCE: 0.4,     // Strength of ally separation
  GATHERER_SEPARATION_RADIUS: 15, // Distance between nebula gatherers
  GATHERER_SEPARATION_FORCE: 0.1  // Gentle separation for gatherers
};

// Combat configuration
export const COMBAT_CONFIG = {
  // Weapon mechanics
  WEAPON_SPREAD_MIN: 0.2,         // Minimum weapon spread
  SKILL_TO_SPREAD_DIVISOR: 50,    // Divisor for skill-based spread
  SPREAD_MULTIPLIER: 10,          // Spread range multiplier
  PROJECTILE_START_OFFSET: 2,     // Distance from soldier edge to spawn projectile
  AUTO_TARGET_RANGE_MULTIPLIER: 2,// Range multiplier for auto-targeting
  
  // Combat distances
  COMBAT_MIN_DISTANCE: 0.7,       // Minimum combat distance (percentage of range)
  COMBAT_MAX_DISTANCE: 0.9,       // Maximum combat distance (percentage of range)
  COMBAT_IDEAL_DISTANCE: 0.85,    // Preferred engagement distance (percentage of range)
  COMBAT_STRAFE_DISTANCE: 0.8,    // Ideal strafing distance
  TARGET_SWITCH_RANGE: 1.5,       // Range to check for better targets
  
  // Damage and effects
  COURAGE_DAMAGE_REDUCTION: 10,   // Courage lost when taking damage
  DAMAGE_INDICATOR_DECAY: 0.05,   // Damage indicator fade speed
  DAMAGE_INDICATOR_RADIUS: 8,     // Damage indicator effect radius
  DAMAGE_INDICATOR_SCALE: 0.45,   // Damage indicator scale factor
  
  // Skill and accuracy
  SKILL_INCREASE_PER_LEVEL: 20,   // Skill points gained per level
  MAX_SKILL_CAP: 100,             // Maximum skill value
  FIRE_RATE_IMPROVEMENT: 0.8,     // Fire rate multiplier per level
  MAX_PREDICTION_ACCURACY: 0.85,  // Maximum targeting prediction accuracy
  SLOW_FIRE_MULTIPLIER: 1.5,      // Fire rate penalty when retreating
  
  // Ammo management
  AMMO_DONATION_PERCENTAGE: 0.25, // Percentage of ammo to donate
  MISSILE_DONATION_AMOUNT: 2, // Number of missiles worth to donate
  HIGH_LEVEL_DONATION_MULTIPLIER: 2.0, // Level 5+ soldiers donate more ammo
  CORPSE_AMMO_GRANTED: 25 // Ammo granted when looting a corpse
};

// Animation and visual effects configuration
export const ANIMATION_CONFIG = {
  // Effect speeds
  MUZZLE_FLASH_DECAY: 0.1,        // Base muzzle flash decay rate
  MUZZLE_FLASH_LEVEL_BONUS: 0.3,  // Level multiplier for flash decay
  MUZZLE_FLASH_BLUR: 20,          // Muzzle flash shadow blur
  
  // Resource animations
  RESOURCE_PULSE_SPEED: 0.1,      // Resource pulse animation speed
  RESOURCE_PICKUP_SPEED: 0.08,    // Resource collection animation speed
  RESOURCE_FINAL_SCALE: 0.2,      // Final scale when collected (20%)
  
  // Corpse effects
  CORPSE_FADE_TIME: 5000,         // Time to fade out corpse (ms)
  
  // Nebula effects
  NEBULA_PARTICLE_COUNT: 20,      // Number of particles in nebula
  NEBULA_CLOUD_PHASE_SPEED: 0.02, // Cloud animation speed
  NEBULA_PARTICLE_ANGLE_SPEED: 0.01, // Particle rotation speed
  NEBULA_PARTICLE_DISTANCE_SPEED: 0.1, // Particle movement speed
  NEBULA_PARTICLE_SIZE_MIN: 1,    // Minimum particle size
  NEBULA_PARTICLE_SIZE_MAX: 3,    // Maximum particle size
  NEBULA_PARTICLE_OPACITY_MIN: 0.3, // Minimum particle opacity
  NEBULA_PARTICLE_OPACITY_MAX: 0.7, // Maximum particle opacity
  NEBULA_PARTICLE_SPEED_MIN: 0.3, // Minimum particle animation speed
  NEBULA_PARTICLE_SPEED_MAX: 0.6, // Maximum particle animation speed
  
  // Crystal effects
  CRYSTAL_GLOW_BASE: 0.5,         // Base glow intensity
  CRYSTAL_GLOW_VARIATION: 0.3,    // Glow variation amount
  CRYSTAL_GLOW_SPEED: 2,          // Glow pulse speed
  CRYSTAL_ROTATION_SPEED: 0.02,   // Crystal rotation speed
  
  // Spawn animations
  RESOURCE_SPAWN_SPEED: 0.05,     // Resource spawn animation (20 frames)
  BASE_SPAWN_SPEED: 0.03,         // Base spawn animation (33 frames)
  SOLDIER_SPAWN_FADE_SPEED: 0.1,  // Soldier fade-in speed
  
  // Base effects
  BASE_DELIVERY_PULSE_DECAY: 0.05 // Resource delivery pulse decay
};

// UI and rendering configuration
export const UI_CONFIG = {
  // Font specifications
  FONT_SMALL: '8px \'Berkeley Mono\', \'Courier New\', monospace',
  FONT_MEDIUM: '10px \'Berkeley Mono\', \'Courier New\', monospace',
  FONT_LARGE: '12px \'Berkeley Mono\', \'Courier New\', monospace',
  
  // Debug rendering
  DEBUG_ALPHA: 0.5,               // Alpha for debug lines
  DEBUG_LINE_WIDTH: 1,            // Width of debug lines
  PERCEPTION_CIRCLE_ALPHA: 0.2,   // Alpha for perception circles
  BASE_PERCEPTION_ALPHA: 0.15,    // Alpha for base perception circles
  PREDICTION_MARKER_ALPHA: 0.8,   // Alpha for prediction markers
  PREDICTION_MARKER_SIZE: 5,      // Size of prediction X marker
  PREDICTION_MARKER_WIDTH: 10,    // Width of prediction X
  PREDICTION_MARKER_HEIGHT: 2,    // Height of prediction X lines
  
  // Base rendering
  BASE_CIRCLE_ALPHA: 0.3,         // Alpha for base circle fill
  BASE_BORDER_WIDTH: 3,           // Width of base border
  BASE_MARGIN: 10,                // Margin around bases
  
  // Health/status bars
  AMMO_LOW_THRESHOLD: 0.3,        // Threshold for low ammo warning
  HEALTH_LOW_COLOR: '#ff4444',    // Color for low health/ammo
  
  // Text offsets
  STATE_TEXT_OFFSET_X: 5,         // X offset for state text
  RESOURCE_TYPE_OFFSET_Y: 10,     // Y offset for resource type
  LEVEL_TEXT_OFFSET_Y: 8,         // Y offset for level text
  COURAGE_BAR_OFFSET_Y: 10,       // Y offset for courage bar
  BASE_STATS_OFFSET_Y: 15,        // Y offset for base stats
  
  // Visual thresholds
  HIGH_LEVEL_THRESHOLD: 2,        // Level to show as high level
  MAX_LEVEL_THRESHOLD: 15         // Level to show as max level
};

// Time and threshold configuration
export const THRESHOLD_CONFIG = {
  // Health thresholds
  HEALTH_READY_THRESHOLD: 0.8,    // Health percentage to be "ready"
  HEALTH_CRITICAL_THRESHOLD: 0.5, // Critical health for decisions
  HEALTH_CORPSE_SEEK_THRESHOLD: 0.6, // Health to prioritize corpses
  
  // Courage thresholds
  COURAGE_READY_THRESHOLD: 0.3,   // Minimum courage to leave base
  COURAGE_RESUPPLY_THRESHOLD: 0.8,// Courage needed for resupply state
  COURAGE_RECOVERY_RATE: 0.001,   // Courage recovery per ms
  COURAGE_RECOVERY_DIVISOR: 100,  // Divisor for courage recovery at base
  
  // Ammo thresholds
  AMMO_READY_THRESHOLD: 0.7,      // Ammo percentage to be "ready"
  AMMO_DONATION_THRESHOLD: 0.8,   // Ammo level to donate to base
  AMMO_LOW_THRESHOLD: 0.3,        // Low ammo threshold
  RADIO_MIN_HEALTH_RATIO: 0.55,   // Minimum health ratio to accept assist calls
  RADIO_MIN_AMMO_RATIO: 0.4,      // Minimum ammo ratio to accept assist calls
  
  // Base thresholds
  BASE_METAL_LOW_THRESHOLD: 0.3,  // Metal percentage for gathering priority
  BASE_CENTER_DISTANCE: 5,        // Distance to be "at center"
  
  // Combat retreat thresholds
  RETREAT_LEVEL_DIFFERENCE: 2,    // Level difference to consider retreat
  RETREAT_BERSERKER_HEALTH: 0.8,  // Health to retreat from berserker
  RETREAT_LEVEL3_HEALTH: 0.7,     // Health to retreat from +3 level enemy
  RETREAT_LEVEL2_HEALTH: 0.5,     // Health to retreat from +2 level enemy
  RETREAT_TARGET_HEALTH: 0.6,     // Enemy health comparison for retreat
  
  // Resource thresholds
  CORPSE_NEAR_KILL_DISTANCE: 50,  // Distance to check for corpse after kill
  CORPSE_FLEE_COLLECTION_RANGE: 2,// Range multiplier for corpse collection while fleeing
  BASE_THREAT_DETECTION_RANGE: 150,// Range to detect threats to base
  BASE_DEFEND_RANGE: 200,         // Range to defend base
  
  // Spawn and timing
  SPAWN_COOLDOWN: 1000,           // Cooldown between unit spawns (ms)
  INITIAL_EVENT_DELAY: 100,       // Delay before first AI event (ms)
  RETREAT_COOLDOWN_DECAY: 16,     // Retreat cooldown decay per frame
  RETREAT_COOLDOWN_DURATION: 3000,// Retreat cooldown duration (ms)
  
  // Patrol distances
  PATROL_IDEAL_DISTANCE: 2,       // Ideal patrol distance (base size multiplier)
  PATROL_MAX_DISTANCE: 1.5,       // Max patrol distance multiplier
  PATROL_MIN_DISTANCE: 0.5,       // Min patrol distance multiplier
  
  // Frame calculations
  TARGET_FPS: 60,                 // Target frames per second
  MS_PER_FRAME: 16.67             // Milliseconds per frame at 60fps
};

// Canvas configuration (for safe direction calculations)
export const CANVAS_CONFIG = {
  DEFAULT_WIDTH: 800,             // Default canvas width
  DEFAULT_HEIGHT: 600,            // Default canvas height
  RESOURCE_SPAWN_MARGIN: 20,      // Margin for resource spawning
  NEBULA_SPAWN_MARGIN: 50         // Larger margin for nebulas
};

// Three.js Renderer Configuration
export const THREE_RENDERER_CONFIG = {
  SCENE: {
    BACKGROUND_COLOR: 0x000000
  },
  
  LIGHTING: {
    AMBIENT: {
      COLOR: 0xffffff,
      INTENSITY: 0.1
    },
    DIRECTIONAL: {
      COLOR: 0xffffff,
      INTENSITY: 1.5,
      POSITION: { x: 200, y: -200, z: 300 }
    },
    SPOTLIGHT: {
      COLOR: 0xffffff,
      INTENSITY: 4.0,
      DISTANCE: 600,
      ANGLE: Math.PI / 3,
      PENUMBRA: 0.3,
      DECAY: 1,
      POSITION_Z: 200,
      FOLLOW_Z: 150
    },
    CURSOR: {
      INTENSITY_INCREMENT: 100,
      MAX_INTENSITY: 100000,
      DISTANCE_INCREMENT: 50,
      MIN_DISTANCE: 50,
      MAX_DISTANCE: 1000,
      COLOR_WHITE: 0xffffff,
      COLOR_ORANGE: 0xff6600,
      COLOR_YELLOW: 0xffff00
    },
    MISSILE: {
      INTENSITY: 10000,
      DISTANCE: 600,
      Z_OFFSET: 10,
      SHADOW_MAP_SIZE: 1024,
      SHADOW_NEAR: 0.5,
      SHADOW_FAR: 600,
      PULSE_AMPLITUDE: 0.3
    },
    EXPLOSION: {
      MAX_INTENSITY: 20000,
      COLOR: 0xffff00,
      BASE_DISTANCE: 600,
      EXPANSION_DISTANCE: 400
    }
  },
  
  SHADOW: {
    MAP_SIZE: 2048,
    CAMERA_NEAR: 0.5,
    CAMERA_FAR: 1000,
    CAMERA_SIZE: 600
  },
  
  CAMERA: {
    NEAR: 0.1,
    FAR: 1000,
    POSITION_Z: 500,
    LOOK_AT: { x: 0, y: 0, z: 0 }
  },
  
  RENDERER: {
    TONE_MAPPING_EXPOSURE: 1.0
  },
  
  GROUND: {
    SIZE: 2000,
    COLOR: 0x333333,
    ROUGHNESS: 0.5,
    METALNESS: 0.0,
    Z_POSITION: -10
  },
  
  GEOMETRY: {
    SOLDIER: { width: 6, length: 10, height: 20 },
    PROJECTILE: { radius: 2, length: 6, capSegments: 4, radialSegments: 8 },
    MISSILE: { radius: 3, height: 12, radialSegments: 6 },
    BASE: {
      PIPE_HEIGHT: 5,
      OUTER_RADIUS: 1,
      INNER_RADIUS: 0.85,
      SEGMENTS: 32
    },
    METAL: {
      SIZE: 12
    },
    NEBULA: {
      RADIUS: 25,
      SEGMENTS: 32
    },
    CRYSTAL: { radius: 15, detail: 0 },
    CORPSE: { width: 10, height: 10, depth: 2 }
  },
  
  MATERIALS: {
    TEAM: {
      ROUGHNESS: 0.7,
      METALNESS: 0.1,
      EMISSIVE_BLACK: 0x000000
    },
    METAL: {
      COLOR: 0x888888,
      METALNESS: 0.8,
      ROUGHNESS: 0.2
    },
    NEBULA: {
      EMISSIVE: 0xff00ff,
      EMISSIVE_INTENSITY: 0.2,
      ROUGHNESS: 1.0,
      METALNESS: 0,
      OPACITY: 1.0
    },
    CRYSTAL: {
      COLOR: 0x00ffff,
      EMISSIVE_BLACK: 0x000000,
      OPACITY: 0.8,
      ROUGHNESS: 0.3,
      METALNESS: 0.2
    },
    CORPSE: {
      COLOR: 0x444444,
      ROUGHNESS: 0.9,
      METALNESS: 0.1
    },
    BASE: {
      PIPE_OPACITY: 0.3,
      FLOOR_OPACITY: 0.25,
      DAMAGED_WALL_OPACITY: 0.3,
      MIN_WALL_OPACITY: 0.1,
      MIN_RING_OPACITY: 0.5
    }
  },
  
  Z_POSITIONS: {
    SOLDIER: 20,
    MISSILE: 8,
    PROJECTILE: 5,
    NEBULA: 2,
    RESOURCE: 15,
    CORPSE: 1,
    BASE_PIPE_CENTER: 2.5,
    BASE_RING: 5,
    BASE_FLOOR: 0,
    CURSOR_LIGHT: 30
  },
  
  ANIMATIONS: {
    SOLDIER: {
      LEVEL_SCALE_MULTIPLIER: 0.1,
      WOBBLE_SPEED: 0.002,
      WOBBLE_AMPLITUDE: 0.05
    },
    BERSERKER: {
      EMISSIVE_BASE: 0.3,
      GLOW_SPEED: 0.01,
      GLOW_AMPLITUDE: 0.2
    },
    DAMAGE: {
      FLASH_INTENSITY: 0.5,
      FLASH_COLOR: 0xff0000
    },
    BASE: {
      DELIVERY_PULSE_SCALE: 0.2
    },
    PROJECTILE: {
      EMISSIVE_INTENSITY: 0.1
    },
    METAL: {
      ROTATION_SPEED_RANGE: 0.002
    },
    CRYSTAL: {
      ROTATION_SPEED: 0.001,
      WOBBLE_AMPLITUDE: 0.1,
      GLOW_BASE: 0.3,
      GLOW_SPEED: 0.002,
      GLOW_AMPLITUDE: 0.1
    },
    NEBULA: {
      GLOW_BASE: 0.2,
      GLOW_SPEED: 0.0015,
      GLOW_AMPLITUDE: 0.1
    },
    RESOURCE: {
      PULSE_SPEED: 0.001,
      PULSE_AMPLITUDE: 0.1,
      PULSE_BASE: 1
    },
    CORPSE: {
      PULSE_SPEED: 0.003,
      PULSE_AMPLITUDE: 0.2,
      PULSE_BASE: 0.8,
      ROTATION: Math.PI / 4
    }
  },
  
  PARTICLES: {
    NEBULA: {
      COUNT: 40,
      MAX_DISTANCE: 37.5,
      ANGLE_SPEED: 0.01,
      DISTANCE_SPEED: 0.1,
      SPEED_MIN: 0.3,
      SPEED_MAX: 0.4,
      SIZE_MIN: 1,
      SIZE_MAX: 2,
      OPACITY_MIN: 0.3,
      OPACITY_MAX: 0.4,
      Z_MIN: 2,
      Z_MAX: 5,
      GEOMETRY_SIZE: 2,
      OPACITY_COLOR: 0xff66cc
    },
    EXHAUST: {
      GEOMETRY: { radius: 2, widthSegments: 4, heightSegments: 4 },
      OPACITY: 0.8,
      MISSILE_LENGTH: 15,
      SPREAD: 3,
      VELOCITY_MULTIPLIER: 2,
      VELOCITY_RANDOM: 1,
      DECAY_RATE: 0.05,
      OPACITY_MULTIPLIER: 0.8,
      PER_FRAME: 3
    }
  },
  
  EXPLOSIONS: {
    MISSILE: {
      PARTICLE_COUNT: 20,
      PARTICLE_GEOMETRY: { radius: 3, widthSegments: 4, heightSegments: 4 },
      YELLOW_THRESHOLD: 10,
      COLOR_YELLOW: 0xffff00,
      COLOR_ORANGE: 0xff4400,
      SPEED_MIN: 3,
      SPEED_MAX: 5,
      Z_VELOCITY_MAX: 3,
      DECAY_RATE: 0.06,
      SCALE_MIN: 1,
      SCALE_MAX: 0.5,
      Z_POSITION: 10,
      SHOCKWAVE: {
        GEOMETRY: { innerRadius: 1, outerRadius: 2, segments: 32 },
        OPACITY: 0.8,
        DECAY_RATE: 0.08,
        Z_POSITION: 1
      }
    },
    UNIT: {
      PARTICLE_COUNT: 12,
      PARTICLE_GEOMETRY: { radius: 1.5, widthSegments: 4, heightSegments: 4 },
      SPEED_MIN: 1,
      SPEED_MAX: 1.5,
      Z_VELOCITY_MIN: 0.5,
      Z_VELOCITY_RANGE: 1,
      DECAY_RATE: 0.06,
      SCALE_MIN: 1.2,
      SCALE_RANGE: 0.6,
      FLASH: {
        GEOMETRY: { radius: 5, widthSegments: 8, heightSegments: 8 },
        COLOR: 0xff8800,
        OPACITY: 0.9,
        EMISSIVE: 0xff6600,
        INTENSITY: 2.0,
        DECAY: 0.125,
        SCALE: 1.5
      }
    },
    PROJECTILE: {
      PARTICLE_COUNT: 8,
      PARTICLE_GEOMETRY: { radius: 1, widthSegments: 4, heightSegments: 4 },
      SPEED_MIN: 1,
      SPEED_MAX: 2,
      Z_VELOCITY_MAX: 1,
      DECAY_RATE: 0.15,
      SCALE_MIN: 0.8,
      SCALE_RANGE: 0.4,
      Z_POSITION: 6,
      FLASH: {
        GEOMETRY: { radius: 3, widthSegments: 8, heightSegments: 8 },
        COLOR: 0xffffff,
        OPACITY: 0.9,
        INTENSITY: 2.0,
        DECAY: 0.25,
        Z_POSITION: 7
      }
    }
  },
  
  UI: {
    NEBULA_TEXTURE_SIZE: 256,
    NEBULA_GRADIENT_CENTER: 128,
    TEXT_SPRITE: {
      CANVAS_WIDTH: 256,
      CANVAS_HEIGHT: 128,
      FONT_SIZE: 48,
      FONT: 'bold 48px monospace',
      SCALE: { x: 40, y: 20, z: 1 },
      COLOR_WHITE: '#ffffff',
      COLOR_GREEN: '#66ff66',
      COLOR_YELLOW: '#ffff66',
      COLOR_RED: '#ff6666',
      COLOR_DEPLETED: '#ff0000'
    },
    TEXT_OFFSETS: {
      GATHERER_Y: 35,
      PERCENT_Y: 50,
      Z: 20
    },
    NEBULA_THRESHOLDS: {
      HIGH: 50,
      MID: 25,
      MAX_GATHERERS: 5
    }
  },
  
  DEBUG: {
    LINE_COLOR: 0xff0000,
    LINE_OPACITY: 0.5
  },
  
  MISC: {
    RING_OPACITY_MULTIPLIER: 0.6,
    FLASH_OPACITY_MULTIPLIER: 0.8,
    EXPLOSION_PARTICLE_SCALE_MULTIPLIER: 2,
    GRADIENT_COLORS: {
      NEBULA_CENTER: 'rgba(255, 100, 200, 0.6)',
      NEBULA_MID: 'rgba(150, 50, 255, 0.3)',
      NEBULA_EDGE: 'rgba(100, 0, 200, 0)'
    }
  }
};

// Load custom config overrides from localStorage
function loadCustomConfig() {
  try {
    const data = JSON.parse(localStorage.getItem('customConfig'));
    if (!data) return;
    Object.assign(GAME_CONFIG, data.GAME_CONFIG || {});
    Object.assign(DEBUG_CONFIG, data.DEBUG_CONFIG || {});
    Object.assign(DEBUG_VISUALIZATION_CONFIG, data.DEBUG_VISUALIZATION_CONFIG || {});
    Object.assign(PROJECTILE_CONFIG, data.PROJECTILE_CONFIG || {});
    Object.assign(AI_CONFIG, data.AI_CONFIG || {});
    Object.assign(TEAM_CONFIG, data.TEAM_CONFIG || {});
    Object.assign(RENDER_CONFIG, data.RENDER_CONFIG || {});
    Object.assign(BASE_CONFIG, data.BASE_CONFIG || {});
    Object.assign(RESOURCE_CONFIG, data.RESOURCE_CONFIG || {});
    Object.assign(THREE_RENDERER_CONFIG, data.THREE_RENDERER_CONFIG || {});
  } catch (e) {
    console.warn('Failed to load custom config', e);
  }
}

loadCustomConfig();

// Utility to get all current config values
export function getAllConfig() {
  return {
    GAME_CONFIG,
    DEBUG_CONFIG,
    DEBUG_VISUALIZATION_CONFIG,
    PROJECTILE_CONFIG,
    AI_CONFIG,
    TEAM_CONFIG,
    RENDER_CONFIG,
    BASE_CONFIG,
    RADIO_CONFIG,
    RESOURCE_CONFIG,
    THREE_RENDERER_CONFIG,
    MOVEMENT_CONFIG,
    COMBAT_CONFIG,
    ANIMATION_CONFIG,
    UI_CONFIG,
    THRESHOLD_CONFIG,
    BERSERKER_CONFIG
  };
}

// Help text system for configuration values
export const CONFIG_HELP = {
  // GAME_CONFIG help text
  GAME_CONFIG: {
    BASE_SPEED: 'Movement speed for soldiers (pixels per frame)',
    BASE_SIZE: 'Radius of soldier collision circle',
    BASE_HEALTH: 'Starting health for level 1 soldiers',
    BASE_WEAPON_RANGE: 'Attack range for soldiers',
    BASE_DAMAGE: 'Damage per hit for level 1 soldiers',
    FLEE_HEALTH_THRESHOLD: 'Health percentage to trigger fleeing (0.3 = 30%)',
    LEVEL_UP_MULTIPLIER: 'Stat multiplier per level (1.2 = 20% increase)',
    MAX_LEVEL: 'Maximum soldier level',
    INITIAL_UNITS_PER_TEAM: 'Starting soldiers per team',
    SPAWN_MARGIN: 'Distance from screen edges for initial spawn',
    AMMO_CAPACITY: 'Maximum shots before needing resupply',
    RESOURCE_CARRY_SPEED_MULTIPLIER: 'Speed reduction when carrying resources (0.6 = 60% speed)',
    MAX_TOTAL_UNITS: 'Global unit limit across all teams',
    MAX_UNITS_PER_TEAM: 'Maximum units per individual team'
  },
  
  // DEBUG_CONFIG help text
  DEBUG_CONFIG: {
    targetingLines: 'Show red lines between soldiers and targets',
    predictedPositions: 'Show white X marks for aim prediction',
    stats: 'Show performance statistics'
  },
  
  // DEBUG_VISUALIZATION_CONFIG help text
  DEBUG_VISUALIZATION_CONFIG: {
    showPerceptionRadius: 'Display perception range circles',
    showWeaponRadius: 'Display weapon range circles',
    showUnitState: 'Show current AI state above units',
    showTargetLines: 'Draw lines to current targets',
    showPredictedPositions: 'Show predicted target positions',
    showPathfinding: 'Display pathfinding information',
    showCollisionBoxes: 'Show collision boundaries',
    showHealthBars: 'Display health bars above units',
    showAmmoCount: 'Show remaining ammo count'
  },
  
  // AI_CONFIG help text
  AI_CONFIG: {
    FIRE_RATE: 'Base milliseconds between shots',
    MIN_FIRE_RATE: 'Minimum fire rate for high-level soldiers',
    PERCEPTION_RANGE: 'Base distance soldiers can detect enemies',
    DECISION_INTERVAL: 'Milliseconds between AI decisions',
    COURAGE_BASE: 'Starting courage value',
    COURAGE_RECOVERY_RATE: 'Courage recovery per frame',
    FLEE_DURATION: 'Minimum frames to flee',
    PREDICTION_ACCURACY: 'Base accuracy for aim prediction (0-1)',
    SEPARATION_RADIUS: 'Distance to maintain from teammates',
    SEPARATION_FORCE: 'Strength of separation behavior',
    CORPSE_COLLECTION_RADIUS: 'Distance to collect corpses',
    CORPSE_SEEK_PRIORITY: 'How much AI prioritizes corpses (0-1)',
    BASE_AVOIDANCE_RADIUS: 'Distance to avoid enemy bases',
    BASE_TARGETING_MIN_LEVEL: 'Min level to opportunistically attack bases',
    BASE_HUNTING_MIN_LEVEL: 'Min level to actively hunt bases'
  },
  
  // COMBAT_CONFIG help text
  COMBAT_CONFIG: {
    WEAPON_SPREAD_MIN: 'Minimum weapon spread angle',
    SKILL_TO_SPREAD_DIVISOR: 'Higher = less spread improvement with skill',
    PROJECTILE_START_OFFSET: 'Distance from soldier edge to spawn bullets',
    COMBAT_MIN_DISTANCE: 'Minimum combat range (% of weapon range)',
    COMBAT_MAX_DISTANCE: 'Maximum combat range (% of weapon range)',
    COURAGE_DAMAGE_REDUCTION: 'Courage lost when taking damage',
    SKILL_INCREASE_PER_LEVEL: 'Skill points gained per level',
    MAX_SKILL_CAP: 'Maximum skill value',
    FIRE_RATE_IMPROVEMENT: 'Fire rate multiplier per level',
    MAX_PREDICTION_ACCURACY: 'Maximum targeting prediction accuracy',
    AMMO_DONATION_PERCENTAGE: 'Percentage of ammo to donate to bases',
    CORPSE_AMMO_GRANTED: 'Ammo granted when looting a corpse'
  },
  
  // RESOURCE_CONFIG help text
  RESOURCE_CONFIG: {
    METAL_SPAWN_INTERVAL: 'Milliseconds between metal spawns',
    NEBULA_SPAWN_INTERVAL: 'Milliseconds between nebula spawns',
    CRYSTAL_SPAWN_INTERVAL: 'Milliseconds between crystal spawns',
    METAL_TO_AMMO_RATIO: 'Ammo generated per metal resource',
    ORGANICS_TO_SPAWN_THRESHOLD: 'Organics needed to spawn a soldier',
    CRYSTAL_UNIT_SPAWNS: 'Number of units spawned by crystal',
    NEBULA_RADIUS: 'Collection radius for nebulas',
    MAX_GATHERERS_PER_NEBULA: 'Maximum simultaneous gatherers',
    ORGANICS_PER_GATHERER_PER_SECOND: 'Collection rate per gatherer',
    METAL_VALUE: 'Metal units collected per resource',
    MAX_RESOURCES_ON_MAP: 'Maximum resources allowed on map'
  },
  
  // BASE_CONFIG help text
  BASE_CONFIG: {
    HEALTH: 'Base starting health',
    RANGE: 'Base defense turret range',
    DAMAGE: 'Base turret damage',
    SPAWN_INTERVAL: 'Milliseconds between soldier spawns',
    REPAIR_THRESHOLD: 'Maximum level required to repair bases',
    PRIORITY_DURATION: 'How long priority states last',
    AMMO_LOW_THRESHOLD: 'Ammo percentage to request metal',
    AMMO_CRITICAL_THRESHOLD: 'Ammo percentage for critical state',
    HEAL_RATE: 'Health restored per frame when healing',
    RESUPPLY_RATE: 'Ammo restored per frame when resupplying'
  },
  
  // MOVEMENT_CONFIG help text
  MOVEMENT_CONFIG: {
    SOLDIER_SPEED_MIN: 'Minimum speed variation multiplier',
    SOLDIER_SPEED_MAX: 'Maximum speed variation multiplier',
    LEVEL_SPEED_MULTIPLIER: 'Speed increase per level',
    FLEE_SPEED_MULTIPLIER: 'Speed boost when fleeing',
    URGENT_SPEED_MULTIPLIER: 'Speed for urgent movement',
    RESOURCE_APPROACH_SPEED: 'Speed when approaching distant resources',
    RETURN_RESOURCE_SPEED: 'Speed when returning resources to base',
    MAX_TURN_RATE: 'Maximum radians per frame for turning',
    ALLY_SEPARATION_RADIUS: 'Distance to maintain from allies',
    ALLY_SEPARATION_FORCE: 'Strength of ally separation'
  },
  
  // PROJECTILE_CONFIG help text
  PROJECTILE_CONFIG: {
    BASE_SPEED: 'Projectile travel speed',
    TRAIL_LENGTH: 'Length of projectile trail effect',
    MAX_LIFETIME: 'Maximum frames before expiration',
    MISSILE_SIZE_MULTIPLIER: 'Size multiplier for base missiles',
    MISSILE_TRAIL_LENGTH: 'Trail length for missiles',
    MISSILE_EXPLOSION_RADIUS: 'Explosion effect radius',
    MISSILE_EXPLOSION_DURATION: 'Frames for explosion animation'
  },
  
  // RENDER_CONFIG help text
  RENDER_CONFIG: {
    HEALTH_BAR_HEIGHT: 'Height of health bars in pixels',
    COURAGE_BAR_HEIGHT: 'Height of courage bars in pixels',
    EDGE_MARGIN: 'Distance from screen edge to avoid',
    SKILL_DOT_SIZE: 'Size of skill indicator dots',
    SKILL_DOT_SPACING: 'Spacing between skill dots'
  },
  
  // ANIMATION_CONFIG help text
  ANIMATION_CONFIG: {
    MUZZLE_FLASH_DECAY: 'Base muzzle flash fade rate',
    RESOURCE_PULSE_SPEED: 'Resource pulse animation speed',
    CORPSE_FADE_TIME: 'Time to fade out corpse (ms)',
    NEBULA_PARTICLE_COUNT: 'Number of particles in nebula',
    CRYSTAL_GLOW_SPEED: 'Crystal glow pulse speed',
    BASE_DELIVERY_PULSE_DECAY: 'Resource delivery effect fade rate'
  },
  
  // THRESHOLD_CONFIG help text
  THRESHOLD_CONFIG: {
    FLEE_HEALTH_THRESHOLD: 'Health percentage to trigger fleeing',
    LOW_HEALTH_THRESHOLD: 'Health percentage for low health state',
    LOW_AMMO_THRESHOLD: 'Ammo percentage for low ammo state',
    RESOURCE_COLLECTION_RANGE: 'Distance to collect resources',
    STUCK_VELOCITY_THRESHOLD: 'Velocity to consider unit stuck',
    STUCK_TIME_THRESHOLD: 'Time before unit is considered stuck',
    OFF_SCREEN_PREDICTION_LIMIT: 'Max distance for off-screen predictions'
  },
  
  // BERSERKER_CONFIG help text
  BERSERKER_CONFIG: {
    DAMAGE_MULTIPLIER: 'Damage multiplier for max-level units',
    SPEED_MULTIPLIER: 'Speed multiplier for max-level units',
    HEALTH_MULTIPLIER: 'Health multiplier for max-level units',
    GLOW_INTENSITY: 'Visual glow effect intensity',
    REPAIR_RANGE: 'Range to repair destroyed bases',
    REPAIR_TIME: 'Time to repair a base (ms)'
  },
  
  // THREE_RENDERER_CONFIG help text
  THREE_RENDERER_CONFIG: {
    LIGHT: {
      AMBIENT_INTENSITY: 'Overall scene brightness',
      DIRECTIONAL_INTENSITY: 'Main light source brightness',
      MISSILE_INTENSITY: 'Missile light brightness',
      CURSOR_INTENSITY: 'Cursor light brightness (debug)',
      SHADOW_MAP_SIZE: 'Shadow quality (higher = better)',
      SHADOW_CAMERA_SIZE: 'Shadow coverage area'
    },
    CAMERA: {
      POSITION_Z: 'Camera height above battlefield',
      NEAR: 'Near clipping plane',
      FAR: 'Far clipping plane'
    },
    MATERIALS: {
      TEAM: {
        ROUGHNESS: 'Surface roughness (0=shiny, 1=matte)',
        METALNESS: 'Metallic appearance (0-1)'
      }
    }
  },
  
  // UI_CONFIG help text
  UI_CONFIG: {
    FONT_SMALL: 'Small font size specification',
    FONT_MEDIUM: 'Medium font size specification', 
    FONT_LARGE: 'Large font size specification',
    DEBUG_ALPHA: 'Transparency for debug overlays',
    HIGH_LEVEL_THRESHOLD: 'Level to show enhanced visuals',
    MAX_LEVEL_THRESHOLD: 'Level for max-level visual effects'
  }
};

// Utility to get help text for a config value
export function getConfigHelp(configKey, valuePath) {
  const helpSection = CONFIG_HELP[configKey];
  if (!helpSection) return null;
  
  // Handle nested paths (e.g., "LIGHT.AMBIENT_INTENSITY")
  const pathParts = valuePath.split('.');
  let help = helpSection;
  
  for (const part of pathParts) {
    if (help && typeof help === 'object') {
      help = help[part];
    } else {
      return null;
    }
  }
  
  return help || null;
}
